/* Papers and service for one bus (2026-10-28): the classic Maintenance page's
   "Service schedule" and "Cover" cards, in the console. The rules are that
   page's and the server's: a km figure is above 0 or empty (empty clears it),
   the last service reading is not above the bus's latest dash reading, a new
   expiry date is today or later, and a date left untouched is left out of the
   save so the instant on file — which an acknowledgement matches — never moves. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ConsoleModal, fieldInput, fieldSelect, ModalButtons, ModalField } from './Modal';
import { busWithPlate, n0, plateBeside, useToast } from './kit';
import type { FleetModel } from './data/model';
import { MaintRow, nairobiDay, saveVehicle, setComplianceAck, toInputDate, todayYmd } from './data/maintenance';

export type MaintFocus = 'service' | 'insurance' | 'inspection';

interface Form { interval: string; last: string; ins: string; insp: string }

const kmText = (n: number) => `${n0(Math.round(n))} km`;
const kmIn = (n: number | null | undefined) => (n != null && n > 0 ? String(n) : '');
const msOf = (v: unknown): number | null => {
  if (v && typeof (v as { toMillis?: () => number }).toMillis === 'function') return (v as { toMillis: () => number }).toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && Number.isFinite(Date.parse(v))) return Date.parse(v);
  return null;
};
const posNum = (v: unknown) => (typeof v === 'number' && v > 0 ? v : null);

/** What is on file: the maintenance engine's row when it has the bus, else the bus record. */
function onFile(f: FleetModel, busId: string) {
  const row: MaintRow | undefined = (f.maintenance || []).find((m) => m.docId === busId || m.busId === busId);
  const raw = f.buses.find((b) => b.id === busId)?.raw || {};
  const c = row?.config;
  return {
    row,
    interval: c ? c.serviceIntervalKm : posNum(raw.serviceIntervalKm),
    last: c ? c.lastServiceOdometerKm : posNum(raw.lastServiceOdometerKm),
    ins: toInputDate(c ? c.insuranceExpiryMs : msOf(raw.insuranceExpiry)),
    insp: toInputDate(c ? c.inspectionExpiryMs : msOf(raw.inspectionExpiry)),
    odo: c ? c.odometerKm : posNum(raw.odometerKm),
  };
}

export const MaintModal: React.FC<{
  f: FleetModel;
  /** No bus: the dialog starts with a bus picker. */
  busId?: string | null;
  /** 'log': a service was done — the reading on the receipt. 'edit': the dates and the schedule. */
  mode: 'log' | 'edit';
  focus?: MaintFocus;
  onClose: () => void;
}> = ({ f, busId: given, mode, focus, onClose }) => {
  const { toast } = useToast();
  const buses = useMemo(() => [...f.buses].sort((a, b) => a.name.localeCompare(b.name)), [f.buses]);
  const [busId, setBusId] = useState(given || buses[0]?.id || '');
  const stored = onFile(f, busId);
  const formOf = (id: string): Form => {
    const s = onFile(f, id);
    return { interval: kmIn(s.interval), last: mode === 'log' ? '' : kmIn(s.last), ins: s.ins, insp: s.insp };
  };
  const [form, setForm] = useState<Form>(() => formOf(busId));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [acking, setAcking] = useState(false);
  const bus = f.buses.find((b) => b.id === busId);

  const target = useRef<HTMLInputElement>(null);
  // After the dialog's own first-field focus: this effect runs after the child's.
  useEffect(() => { if (focus) target.current?.focus(); }, [focus]);

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((x) => ({ ...x, [k]: k === 'ins' || k === 'insp' ? e.target.value : e.target.value.replace(/[^\d]/g, '') }));

  /* The errors, said under the field before the round trip. */
  const today = todayYmd();
  const errs: Partial<Record<keyof Form, string>> = {};
  if (form.interval && !(Number(form.interval) > 0)) errs.interval = 'More than 0 km — or leave it empty if this bus has no schedule.';
  const lastOnFile = Number(form.last) > 0 && Number(form.last) === stored.last;
  if (mode === 'log' && !form.last && tried) errs.last = 'Type the reading on the workshop receipt.';
  else if (form.last && !(Number(form.last) > 0)) errs.last = 'More than 0 km — or leave it empty if you don’t have the receipt.';
  else if (form.last && !lastOnFile && stored.odo && stored.odo > 0 && Math.round(Number(form.last)) > Math.round(stored.odo)) {
    errs.last = `Higher than this bus’s latest reading (${kmText(stored.odo)}). Check the receipt for a typo.`;
  }
  if (form.ins && form.ins < today && form.ins !== stored.ins) errs.ins = 'Today or later.';
  if (form.insp && form.insp < today && form.insp !== stored.insp) errs.insp = 'Today or later.';
  const bad = Object.keys(errs).length > 0;

  const submit = () => {
    setTried(true);
    if (busy || !bus) return;
    if (bad || (mode === 'log' && !form.last)) return;
    setBusy(true);
    const untouched = (next: string, was: string) => (next === was ? undefined : next || null);
    saveVehicle(f.org.code, stored.row?.docId || bus.id, {
      serviceIntervalKm: form.interval || null,
      lastServiceOdometerKm: form.last || null,
      ...(mode === 'edit' ? { insuranceExpiry: untouched(form.ins, stored.ins), inspectionExpiry: untouched(form.insp, stored.insp) } : {}),
    })
      .then((r) => {
        if (!r.ok) { toast({ kind: 'error', text: r.message }); return; }
        toast({ kind: 'success', text: mode === 'log' ? `Service logged for ${bus.name}` : `${bus.name} saved` });
        f.refreshMaintenance();
        onClose();
      })
      .catch(() => toast({ kind: 'error', text: 'Couldn’t save. Check the connection and try again.' }))
      .finally(() => setBusy(false));
  };

  const ack = (undo: boolean) => {
    if (acking || !bus) return;
    setAcking(true);
    setComplianceAck(f.org.code, stored.row?.docId || bus.id, undo)
      .then((r) => {
        if (!r.ok) { toast({ kind: 'error', text: r.message }); return; }
        toast({ kind: 'success', text: undo ? 'Reminders are back on' : 'Acknowledged. Reminders are off for 30 days.' });
        f.refreshMaintenance();
      })
      .catch(() => toast({ kind: 'error', text: 'Couldn’t save. Check the connection and try again.' }))
      .finally(() => setAcking(false));
  };

  const dash = stored.odo && stored.odo > 0 ? Math.round(stored.odo) : null;
  const dashButton = (
    <button type="button" className="btn s b-line" style={{ alignSelf: 'flex-start' }} disabled={!dash}
      onClick={() => dash && setForm((x) => ({ ...x, last: String(dash) }))}>
      {dash ? `Use the latest dash reading · ${kmText(dash)}` : 'No dash reading yet'}
    </button>
  );
  const intervalField = (
    <ModalField label="Service every (km)" error={errs.interval} hint={mode === 'log' ? 'From the workshop. Leave it empty if this bus has no schedule.' : undefined}>
      {(id, ring) => <input id={id} {...ring} ref={focus === 'service' && mode === 'edit' ? target : undefined} style={fieldInput} inputMode="numeric" value={form.interval} placeholder="5000" onChange={set('interval')} />}
    </ModalField>
  );
  const lastField = (
    <ModalField label={mode === 'log' ? 'Odometer at the service (km)' : 'Odometer at the last service (km)'} error={errs.last}>
      {(id, ring) => <input id={id} {...ring} ref={mode === 'log' && focus ? target : undefined} style={fieldInput} inputMode="numeric" value={form.last} placeholder="118400" onChange={set('last')} />}
    </ModalField>
  );
  const dateField = (k: 'ins' | 'insp', label: string, kind: MaintFocus) => (
    <ModalField label={label} error={errs[k]}>
      {(id, ring) => <input id={id} {...ring} ref={focus === kind ? target : undefined} type="date" min={today} style={fieldInput} value={form[k]} onChange={set(k)} />}
    </ModalField>
  );

  const row = stored.row;
  const expired = (row?.compliance || []).filter((c) => c.state === 'expired');
  const ackNow = row?.complianceAck || null;

  return (
    <ConsoleModal
      title={mode === 'log' ? 'Log a service' : 'Papers and service'}
      onClose={onClose}
      onSubmit={submit}
      enterHint="save"
      footer={<ModalButtons onCancel={onClose} primary={mode === 'log' ? 'Log service' : 'Save'} onPrimary={submit} busy={busy} disabled={bad} />}
    >
      {!given ? (
        <ModalField label="Bus">
          {(id, ring) => (
            <select id={id} {...ring} style={fieldSelect} value={busId} onChange={(e) => { setBusId(e.target.value); setForm(formOf(e.target.value)); setTried(false); }}>
              {buses.map((b) => <option key={b.id} value={b.id}>{busWithPlate(b)}</option>)}
            </select>
          )}
        </ModalField>
      ) : bus && (
        <p style={{ margin: '-6px 0 0', color: 'var(--muted)', fontSize: 15 }}>
          {bus.name}{plateBeside(bus) ? <> · <span className="mono">{bus.plate}</span></> : null}
          {dash ? ` · last dash reading ${kmText(dash)}` : ''}
        </p>
      )}

      {mode === 'log' ? (
        <>
          <p style={{ margin: 0, fontSize: 15 }}>Type the kilometres on the workshop receipt. The next service is counted from there.</p>
          {lastField}
          {dashButton}
          {intervalField}
        </>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px', minWidth: 0, display: 'flex' }}>{dateField('ins', 'Insurance expiry', 'insurance')}</div>
            <div style={{ flex: '1 1 200px', minWidth: 0, display: 'flex' }}>{dateField('insp', 'NTSA inspection expiry', 'inspection')}</div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px', minWidth: 0, display: 'flex' }}>{intervalField}</div>
            <div style={{ flex: '1 1 200px', minWidth: 0, display: 'flex' }}>{lastField}</div>
          </div>
          {dashButton}
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: 13 }}>
            No receipt? Use the latest dash reading: the count then starts from today, not from the last workshop visit.
          </p>
          {expired.length > 0 && (
            <div style={{ borderRadius: 16, background: 'var(--s2)', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <b style={{ color: 'var(--bad)' }}>Not legal to run</b>
              {expired.map((c) => (
                <span key={c.kind} style={{ fontSize: 15 }}>
                  {c.label} expired{c.atMs ? ` ${nairobiDay(c.atMs)}` : ''}
                  {c.acknowledged && ackNow ? ` — acknowledged by ${ackNow.ackedByName || 'an administrator'} on ${nairobiDay(ackNow.ackedAtMs)}` : ''}
                </span>
              ))}
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                {ackNow
                  ? `Reminders are off until ${nairobiDay(ackNow.untilMs)}, or sooner if a date changes or another paper expires. The bus is still not legal to run.`
                  : 'Renewed? Put the new date above. Acknowledge turns the reminders off for 30 days. It does not make the bus legal to run.'}
              </span>
              <div>
                {ackNow
                  ? <button type="button" className="btn s b-line" disabled={acking} onClick={() => ack(true)}>{acking ? 'Saving…' : 'Undo'}</button>
                  : expired.some((c) => !c.acknowledged) && <button type="button" className="btn s b-line" disabled={acking} onClick={() => ack(false)}>{acking ? 'Saving…' : 'Acknowledge'}</button>}
              </div>
            </div>
          )}
        </>
      )}
    </ConsoleModal>
  );
};
